// @ts-strict-ignore
import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {promisifyModal} from 'common/components/modal/utils';
import {BrowseTrackedEvent} from 'common/report/util/search/tracking';
import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {DummyUrlManager} from 'common/contexts/url-manager/dummy';
import {Visibility} from 'common/components/base/stackable';
import {BrowseMode} from 'common/widgets/browse/types';
import {BrowseEntityTypeFilter} from 'common/report/util/search';
import {CurrentProject} from 'common/report/model-resources/projects';

import {template} from './template';
import css from './index.inline.styl';
import {LoadEntityModalAttrs, LoadEntityModalState} from './types';

export class LoadEntityModal extends Component<
  LoadEntityModalState,
  LoadEntityModalAttrs,
  EmptyObj,
  void,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<LoadEntityModalAttrs> {
    return {
      ...super.attrsSchema,
      dashboard: `json`,
      'default-sort-option': {
        type: `string`,
        description: `sort option to override default`,
        tsType: `SearchSortOption`,
      },
      'entity-type-filter': {
        type: `string`,
        enum: Object.values(BrowseEntityTypeFilter),
      },
      'entities-loading': `boolean`,
      'filter-default-open': `boolean`,
      mode: {type: `string`, enum: Object.values(BrowseMode), default: BrowseMode.Browse},
      open: {type: `boolean`},
      'project-id': `string`,
      'search-text': `string`,
      'user-id': {type: `number`},
      'workspace-id': `number`,
    };
  }

  triggerCloseModal() {
    emitTrackEvent(this, {
      eventName: BrowseTrackedEvent.ClosedModal,
    });
    this.dispatchEvent(new CustomEvent(`close`));
  }

  handleLoadModalStateChange = (ev) => {
    const {state} = ev.detail;
    if (state === Visibility.Closed) {
      this.triggerCloseModal();
    }
  };

  get config(): ConfigOptions<LoadEntityModalState, EmptyObj, IronContextRegistry> {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`urlManager` as const, `currentProject` as const],
      defaultContexts: {
        urlManager: new DummyUrlManager(),
      },
      defaultState: {
        name: ``,
        open: false,
        searchedText: null,
      },
    };
  }

  getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  handleSearchedTextUpdated(searchedText) {
    this.update({searchedText});
  }
}

export const LoadEntityModalEl = defineAndMakeJsxFactory(`mp-load-entity-modal`, LoadEntityModal);

export const LoadEntityModalReact = panel2react(`mp-load-entity-modal`, LoadEntityModal);

export async function loadEntityModalPromise(
  attrs: Partial<LoadEntityModalAttrs>,
  props: any = {},
): Promise<CustomEvent> {
  return promisifyModal({
    tagName: `mp-load-entity-modal`,
    attrs,
    props,
    closeEvents: [`select`, `close`],
  });
}
